import { useState } from "react";
import { Modal, Form, Input, Select, Button } from "antd";

import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";

interface CreatePlanFormValues {
  name: string;
  indication: string;
  phase: string;
}

interface CreatePlanModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (values: CreatePlanFormValues) => void;
}

const PHASE_OPTIONS = [
  { value: "Phase 1", label: "Phase 1" },
  { value: "Phase 1/2", label: "Phase 1/2" },
  { value: "Phase 2", label: "Phase 2" },
  { value: "Phase 2/3", label: "Phase 2/3" },
  { value: "Phase 3", label: "Phase 3" },
  { value: "Phase 4", label: "Phase 4" },
];

export default function CreatePlanModal({
  open,
  onClose,
  onCreate,
}: CreatePlanModalProps) {
  const [form] = Form.useForm<CreatePlanFormValues>();
  const simplified = useFeatureFlag(FEATURE_FLAGS.PLANNING_SIMPLIFIED, true);
  const [loading, setLoading] = useState(false);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      onCreate(values);
      form.resetFields();
      setLoading(false);
    } catch {
      // validation error — do nothing
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={simplified ? "New Scenario" : "New Trial Plan"}
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="create"
          type="primary"
          loading={loading}
          className="bg-[#0C98D0] border-none"
          onClick={handleOk}
        >
          {simplified ? "Create scenario" : "Create Plan"}
        </Button>,
      ]}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" className="mt-4">
        <Form.Item
          name="name"
          label={simplified ? "Scenario Name" : "Plan Name"}
          rules={[
            {
              required: true,
              message: `${simplified ? "Scenario" : "Plan"} name is required`,
            },
          ]}
        >
          <Input placeholder="e.g. NEXAGEN-302 Feasibility" />
        </Form.Item>
        <Form.Item name="indication" label="Indication">
          <Input placeholder="e.g. Treatment-Resistant Depression" />
        </Form.Item>
        <Form.Item name="phase" label="Phase">
          <Select
            placeholder="Select phase"
            options={PHASE_OPTIONS}
            allowClear
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
